import { useParams } from 'react-router-dom'

import { AvailableModules, Features } from 'generated/featureDefinitions'
import { ContactDetailsProjectsPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import ContactsProjectsList from 'modules/finance/components/Contacts/ContactsProjectsList'
import { useContactProjectListPermissions } from 'modules/finance/hooks/useContactProjectListPermissions'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

const ContactsDetailsProjectsPage: React.FC = () => {
  const { contactId } = useParams()
  const { company } = useCurrentSession()

  const { data, loading, error } = useQuery(ContactDetailsProjectsPageDocument, {
    variables: {
      id: Number(contactId),
      companyId: company.id,
    },
  })
  const contact = data?.finance?.contact

  const hasClientFeature = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_clients
  )?.available

  const hasProjectsManagementFeature = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_management
  )?.available

  // If the client doesn't have the projects management feature, we show the upsell no matter what
  // because we're already shown the "Projects" tab
  const seeProjectsUpsell = !hasProjectsManagementFeature
  // If the client has the projects management feature but doesn't have the projects clients feature
  // they are on a legacy plan
  const seeProjectsUpgrade = !hasClientFeature && hasProjectsManagementFeature

  const canManageProjects = data?.projectManagement._policies.projectManage.can ?? false

  const hasProjectCosts = data?.companies.featureAvailabilitiesConnection.nodes.find(
    (node) => node.id === Features.projects_costs
  )

  useEffect(() => {
    track(trackingEvents.accounting.viewContactDetailsPage, {
      view: 'projects',
      projects_upsell: seeProjectsUpsell || seeProjectsUpgrade,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { loading: loadingProjectListPermissions, policies } = useContactProjectListPermissions()

  const showProjectsUpsell = !loading && (seeProjectsUpsell || seeProjectsUpgrade)

  if (showProjectsUpsell) {
    const featureRequested = seeProjectsUpsell
      ? Features.projects_management
      : Features.projects_clients
    const title = seeProjectsUpsell
      ? i18n.t('finance.contacts.contact_details.projects.upsell_title')
      : i18n.t('packaging.upgrade_plan.title')
    const description = seeProjectsUpsell
      ? i18n.t('finance.contacts.contact_details.projects.upsell_description')
      : i18n.t('project_management.spending.table.upsell_message.legacy_plan')
    const requestReason = seeProjectsUpsell
      ? ValidReasons.MissingAddon
      : ValidReasons.UpgradeFromLegacy

    return (
      <UpsellingKit.EmptyState
        moduleRequested={AvailableModules.projects}
        featureRequested={featureRequested}
        requestReason={requestReason}
        requestSource="projects_sales_clients_projects_tab"
        title={title}
        description={description}
        buttonLabel={i18n.t('common.contact_support')}
      />
    )
  }

  return (
    <ResponsivePage.Body
      authorized={canManageProjects}
      error={error}
      loading={loading || loadingProjectListPermissions}
      variant="full"
    >
      {contact && (
        <ContactsProjectsList
          policies={policies}
          contact={contact}
          hasProjectCosts={!!hasProjectCosts}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default ContactsDetailsProjectsPage
